/**
 * 카테고리 키 → 아이콘 매핑 (이모지 금지 정책의 공용 helper).
 *
 * - `categoryIcon` 같은 데이터 필드에는 이모지가 아니라 카테고리 키 문자열을 저장한다.
 *   예) 'restaurant' | 'cafe' | 'hotel' | 'attraction' | 'pin' | 'search' | 'pushpin' …
 * - React 렌더링은 `CategoryIcon` 컴포넌트 사용.
 * - Kakao SDK 처럼 innerHTML 문자열을 요구하는 곳은 `categoryIconHtml(key, size)` 로 SVG 마크업 문자열을 얻어 그대로 삽입.
 *
 * 동일 카테고리 = 동일 SVG. 새 키는 여기 한 곳에만 추가하면 React/SDK 양쪽에서 동시에 반영된다.
 */
import React from 'react';
import {
    IconProps,
    IcPin,
    IcSearch,
    IcPushpin,
    IcRestaurant,
    IcCafe,
    IcBeer,
    IcHotel,
    IcLandmark,
    IcShopping,
    IcBowling,
    IcGym,
    IcPlane,
    IcTrain,
    IcGame,
    IcPalette,
    IcSpa,
    IcMoon,
    IcMic,
    IcFlame,
} from './EmojiIcons';

/** 카테고리 키 → React 컴포넌트 */
export const CATEGORY_ICON_COMPONENT: Record<string, React.FC<IconProps>> = {
    // 음식/음료
    restaurant: IcRestaurant,
    meal_delivery: IcRestaurant,
    meal_takeaway: IcRestaurant,
    food: IcRestaurant,
    cafe: IcCafe,
    bakery: IcCafe,
    bar: IcBeer,
    nightlife: IcMoon,
    // 숙박
    hotel: IcHotel,
    sleeping: IcHotel,
    // 관광/문화
    attraction: IcLandmark,
    sightseeing: IcLandmark,
    landmark: IcLandmark,
    museum: IcPalette,
    discovering: IcPalette,
    // 쇼핑
    shopping: IcShopping,
    // 여가/활동
    activity: IcBowling,
    entertainment: IcBowling,
    bowling: IcBowling,
    amusement_park: IcGame,
    movie_theater: IcGame,
    karaoke: IcMic,
    park: IcLandmark,
    // 운동/스포츠
    sports: IcGym,
    gym: IcGym,
    spa: IcSpa,
    // 교통
    airport: IcPlane,
    train: IcTrain,
    // 일반
    pin: IcPin,
    search: IcSearch,
    pushpin: IcPushpin,
    all: IcFlame,
    default: IcPin,
};

interface CategoryIconProps extends IconProps {
    icon?: string;
}

/** 카테고리 키 → SVG 컴포넌트 (없으면 IcPin) */
export const CategoryIcon: React.FC<CategoryIconProps> = ({ icon, ...rest }) => {
    const Comp = (icon && CATEGORY_ICON_COMPONENT[icon]) || IcPin;
    return <Comp {...rest} />;
};

/**
 * 카테고리 키 → SVG 마크업 문자열 (외부 SDK innerHTML 용).
 *
 * Kakao Maps CustomOverlay 처럼 React 렌더 트리 밖에서 raw HTML 만 받을 수 있는 곳에서 사용.
 * EmojiIcons.tsx 의 디자인과 동일 (stroke 1.8, viewBox 0 0 24 24).
 */
export const categoryIconHtml = (icon: string | undefined, size = 12): string => {
    const path = ICON_SVG_PATH[icon || ''] || ICON_SVG_PATH.default;
    return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${path}</svg>`;
};

/**
 * 카테고리 키 → svg path 마크업 (categoryIconHtml 내부용).
 * EmojiIcons.tsx 의 path 와 동일 좌표계를 유지.
 */
const ICON_SVG_PATH: Record<string, string> = {
    restaurant: '<path d="M7 3v8M5 3v4a2 2 0 0 0 4 0V3M7 11v10M17 3c-1.5 0-2.5 2-2.5 5s1 4 2.5 4 2.5-1 2.5-4-1-5-2.5-5zM17 16v5"/>',
    meal_delivery: '<path d="M7 3v8M5 3v4a2 2 0 0 0 4 0V3M7 11v10M17 3c-1.5 0-2.5 2-2.5 5s1 4 2.5 4 2.5-1 2.5-4-1-5-2.5-5zM17 16v5"/>',
    meal_takeaway: '<path d="M7 3v8M5 3v4a2 2 0 0 0 4 0V3M7 11v10M17 3c-1.5 0-2.5 2-2.5 5s1 4 2.5 4 2.5-1 2.5-4-1-5-2.5-5zM17 16v5"/>',
    food: '<path d="M7 3v8M5 3v4a2 2 0 0 0 4 0V3M7 11v10M17 3c-1.5 0-2.5 2-2.5 5s1 4 2.5 4 2.5-1 2.5-4-1-5-2.5-5zM17 16v5"/>',
    cafe: '<path d="M4 8h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z"/><path d="M17 9h2.5a2.5 2.5 0 0 1 0 5H17M7 3v2M11 3v2"/>',
    bakery: '<path d="M4 8h13v5a5 5 0 0 1-5 5H9a5 5 0 0 1-5-5V8z"/><path d="M17 9h2.5a2.5 2.5 0 0 1 0 5H17M7 3v2M11 3v2"/>',
    bar: '<path d="M5 8h10v11a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8z"/><path d="M15 10h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-3M5 8c0-2 2-3 5-3s5 1 5 3"/>',
    nightlife: '<path d="M20 13.5A8 8 0 0 1 10.5 4 7 7 0 1 0 20 13.5z"/>',
    hotel: '<path d="M3 19v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7"/><path d="M3 16h18M7 10V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3M3 19v1M21 19v1"/>',
    sleeping: '<path d="M3 19v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7"/><path d="M3 16h18M7 10V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v3M3 19v1M21 19v1"/>',
    attraction: '<path d="M12 3l8 4H4l8-4z"/><path d="M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16"/>',
    sightseeing: '<path d="M12 3l8 4H4l8-4z"/><path d="M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16"/>',
    landmark: '<path d="M12 3l8 4H4l8-4z"/><path d="M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16"/>',
    museum: '<path d="M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2s-.8-1.6-.8-2.6 1-1.4 2-1.4H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8z"/>',
    discovering: '<path d="M12 3a9 9 0 0 0 0 18c1.5 0 2-1 2-2s-.8-1.6-.8-2.6 1-1.4 2-1.4H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8z"/>',
    shopping: '<path d="M5 8h14l-1 12H6L5 8z"/><path d="M9 9V7a3 3 0 0 1 6 0v2"/>',
    activity: '<circle cx="10" cy="13" r="7"/><path d="M8 10h.01M11 9h.01M11 12h.01M18 4c1.5 2 1.5 6 0 8M21 3c2 3 2 8 0 11"/>',
    entertainment: '<circle cx="10" cy="13" r="7"/><path d="M8 10h.01M11 9h.01M11 12h.01M18 4c1.5 2 1.5 6 0 8M21 3c2 3 2 8 0 11"/>',
    bowling: '<circle cx="10" cy="13" r="7"/><path d="M8 10h.01M11 9h.01M11 12h.01M18 4c1.5 2 1.5 6 0 8M21 3c2 3 2 8 0 11"/>',
    amusement_park: '<path d="M7 8h10a4 4 0 0 1 4 4l-1 5a2.5 2.5 0 0 1-4.4 1L14 16h-4l-1.6 2A2.5 2.5 0 0 1 4 17l-1-5a4 4 0 0 1 4-4z"/>',
    movie_theater: '<path d="M7 8h10a4 4 0 0 1 4 4l-1 5a2.5 2.5 0 0 1-4.4 1L14 16h-4l-1.6 2A2.5 2.5 0 0 1 4 17l-1-5a4 4 0 0 1 4-4z"/>',
    karaoke: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6"/>',
    park: '<path d="M12 3l8 4H4l8-4z"/><path d="M6 10v7M10 10v7M14 10v7M18 10v7M4 20h16"/>',
    sports: '<path d="M5 9v6M3 11v2M19 9v6M21 11v2M7 12h10M9 8v8M15 8v8"/>',
    gym: '<path d="M5 9v6M3 11v2M19 9v6M21 11v2M7 12h10M9 8v8M15 8v8"/>',
    spa: '<path d="M5 21h14a6 6 0 0 0-14 0z"/><path d="M9 9c0-2 1.5-3 1.5-3M9 9s2-1 2-3M14.5 9c0-2-1.5-3-1.5-3M14.5 9s-2-1-2-3"/>',
    airport: '<path d="M21 15.5L3 11V8l3 1 4-3-1-3 2-1 3 5 5 1.5a1.6 1.6 0 0 1 0 3L13 13l-2 6h-2l1-4-3 .5-1 2H4l1.5-3.5z"/>',
    train: '<rect x="6" y="4" width="12" height="13" rx="3"/><path d="M6 10h12M9 17l-2 3M15 17l2 3"/>',
    pin: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
    search: '<circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/>',
    pushpin: '<path d="M9 3h6l-1 6 3 3v2H7v-2l3-3-1-6z"/><path d="M12 14v7"/>',
    all: '<path d="M12 3c3 4 6 6 6 10a6 6 0 0 1-12 0c0-2 1-3.5 2.5-5C9 9.5 10 8 9.5 6 11 6.5 12 4.5 12 3z"/>',
    default: '<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>',
};
